import React, { useState, useCallback } from 'react';
import moment from 'moment-jalaali';
import {
  convertDigitToProperFormat,
  convertNumbersToProperFormat,
  isANumber,
} from 'utils/number';
import {
  getClassObjectBaseOnPeriodType,
  getEffectiveName,
} from '../SurveyEditor/UniqueQuestion/PeriodTypeSelector/DatePeriod';
import messages from './Messages';
import Icon from 'components/Icon';
import Popover from 'components/Popover';
import ActionLink from 'components/ActionLink';
import LoadingIcon from 'components/LoadingIcon';
import DistinctiveAnswers from 'components/DistinctiveAnswers';
import {
  Item,
  ItemIcon,
  ItemRow,
  ItemRowPart,
  LoadingStyle,
  Mask,
  MyBadge,
  ResponseItemIcons,
  ScoreBadge,
  Title,
} from './styles';
import { ListItem } from '../../../../Chart/styles';
import APopover from '../../../../APopover';
import { black, blue, darkGray } from '../../../../../utils/style/colors';
import { roundToDynamicFloating } from '../../../../Chart/analyseUtils';
import { ITags } from 'store/providers/providerTypes';
import { IUsers } from 'store/users/usersType';
import {
  getTimeScale,
  IAnswersView,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ISurveyItemWithData,
  ISurveyProperties,
  ISurveyResponseView,
  ISurveyView,
  TimeScale,
} from 'store/survey/surveyTypes';
import i18n from '../../../../../i18n';
import { IOffset } from '../SurveyEditor/UniqueQuestion/PeriodTypeSelector/PeriodTypeSelector';
import { NumberOrNull } from '../../../../../store/entity/types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';

export const SINGLE_DAY_BASE_ON_TIME_STAMP = 24 * 60 * 60 * 1000;

export interface ITaskSurveyResponse {
  reponseItem?: ISurveyResponseView;
  surveyItem?: ISurveyProperties;
}

interface IComponentProps {
  tags?: ITags['data'];
  users: IUsers['map'];
  canEdit: boolean;
  canEditSensitive: boolean;
  item: ISurveyItemWithData;
  response: ISurveyResponseView;
  responseId: number;
  thisUserId: number;
  onClick: (responseId: number) => (e?: React.MouseEvent) => void;
  survey: ISurveyView;
  periodType?: TimeScale;
  templates?: { id: number; title: string }[];
  embedded?: boolean;
  onEditResponse: (responseId: number) => (e?: React.MouseEvent) => void;
  createTask: (
    responseId: number,
    templateId: number,
    templateName: string,
    taskId?: number,
    questionConfigs?: ITaskSurveyResponse
  ) => void;
  openDeleteModal: (e?: React.MouseEvent) => void;
  onUpdateResponse: (
    responseId: number,
    state: 'lock' | 'draft'
  ) => Promise<ISurveyResponseView>;
  selectedItem: NumberOrNull | undefined;
  local: string;
}

export const isCurrentDateInTheRangeOfOffset = (
  startDate: number | undefined,
  endDate: number | undefined,
  offset: IOffset | null | undefined
) => {
  const currentDate: Date = new Date();
  if (startDate && endDate) {
    if (!offset) return true;
    startDate = startDate + offset.start * SINGLE_DAY_BASE_ON_TIME_STAMP;
    endDate = endDate + offset.end * SINGLE_DAY_BASE_ON_TIME_STAMP;
    return (
      currentDate.getTime() >= startDate && currentDate.getTime() <= endDate
    );
  }
  return false;
};

export function isEditableInPeriod(
  item: ISurveyItemWithData,
  survey: ISurveyView,
  response: ISurveyResponseView
) {
  const userHaveChangePermission = item.data.changeDataToken || null;
  const fillValidPeriod = survey.fillValidPeriod;
  const offset =
    (isANumber(fillValidPeriod.fromOffsetDaysRelativeToStartOfPeriod) ||
      isANumber(fillValidPeriod.toOffsetDaysRelativeToEndOfPeriod)) &&
    !userHaveChangePermission
      ? ({
          start: fillValidPeriod.fromOffsetDaysRelativeToStartOfPeriod,
          end: fillValidPeriod.toOffsetDaysRelativeToEndOfPeriod,
        } as IOffset)
      : null;
  const periodType = getTimeScale(
    survey.versions[response.surveyVersion].periodType
  );
  const currentDate = new Date();
  const selectedPeriod =
    periodType === null
      ? { start: currentDate.getTime(), end: currentDate.getTime() }
      : getClassObjectBaseOnPeriodType(
          periodType as TimeScale,
          response.effectiveDate!
        );
  const startDate = selectedPeriod!.start;
  const endDate = selectedPeriod!.end;
  return isCurrentDateInTheRangeOfOffset(startDate, endDate, offset);
}

const ResponseItem: React.FC<IComponentProps> = ({
  tags,
  users,
  canEdit,
  canEditSensitive,
  response,
  responseId,
  thisUserId,
  onEditResponse,
  openDeleteModal,
  survey,
  item,
  onClick,
  embedded,
  templates,
  selectedItem,
  onUpdateResponse,
  createTask,
}) => {
  const [loading, setLoading] = useState<'lock' | 'draft' | null>(null);
  const [showTaskPopover, setShowTaskPopover] = useState(false);

  const handleUpdateResponse = useCallback(
    (responseId: number, state: 'lock' | 'draft') => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setLoading(state);
      onUpdateResponse(responseId, state)
        .then(() => {
          setLoading(null);
        })
        .catch(() => {
          setLoading(null);
        });
    },
    [onUpdateResponse]
  );

  const handleClickEvent = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleShowTaskPopover = useCallback(() => {
    setShowTaskPopover(!showTaskPopover);
  }, [showTaskPopover]);

  const getAllRelatedFieldsConfigs = useCallback(() => {
    const version = survey.versions[response.surveyVersion];
    return { responseItem: response, surveyItem: version };
  }, [survey, response]);
  const version = survey.versions[response.surveyVersion];
  const periodType = getTimeScale(
    survey.versions[response.surveyVersion].periodType
  );
  const isEditableForResponse = isEditableInPeriod(item, survey, response);

  const isActive = selectedItem === null ? null : selectedItem === responseId;
  const distinctiveQuestions = Object.values(version.questionMap!).filter(
    (question: IQuestionsView) =>
      question.type === 'multipleChoice' &&
      !question.deleted &&
      (question.distinctive || question.showInTitle) &&
      (!(question as IMultipleChoiceQuestionView).readFromItemTag ||
        !item?.data?.upstreamItemId)
  );
  const titleQuestions = Object.values(version!.questionMap!)
    .filter(
      (question: IQuestionsView) =>
        question.type === 'text' &&
        question.inputType !== 'rich' &&
        (question.showInTitle ||
          !!(
            question.fields &&
            question.fields.find((it) => it.name === 'ShowInTitle')
          ))
    )
    .sort((a, b) => a.order - b.order);
  const isResponder = thisUserId === response.userId;
  const titleAnswers = titleQuestions.reduce(
    (current: string, item: IQuestionsView) => {
      const answer: IAnswersView = response.surveyAnswerList.find(
        (ans: any) => !!ans && ans.questionId === item.id
      )!;
      if (!answer) return current;
      if (current.length > 0) current = current + ' ';
      return current + answer.value;
    },
    ''
  );
  const withInLockEditDuration =
    response.submitDate! + (survey.fillDurationInMinutes || -1) * 60 * 1000 >
    new Date().valueOf();

  const responseScore = response.score;
  const tasks = useSelector((s: RootState) => s.tasks);
  const filteredTasks =
    !!templates?.length && survey.surveyType !== 'Exam' && response
      ? Object.values(tasks.data).filter(
          (task) =>
            task.relatedProviderItems?.find((it) => it.id === item.data.id) &&
            task.fields.find(
              (f: any) =>
                (f.type === 'responseLink' || f.type === 'analyseLink') &&
                f.value === response.id!.toString()
            )
        )
      : [];
  return (
    <Item
      key={response.id}
      onClick={onClick(responseId)}
      isActive={isActive}
      embedded={embedded}
      draft={response.draft}
      className={'hover-area'}
    >
      <ItemRow isTitle={true}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {
            <MyBadge size={6} hollow={true} color={`${black}`}>
              {i18n.language === 'fa'
                ? convertNumbersToProperFormat(responseId)
                : responseId}
            </MyBadge>
          }
          {item.data.instanceType === 'User' && (
            <MyBadge size={12} style={{ justifyContent: 'right' }}>
              <Icon type="user" style={{ padding: '0 5px' }} />{' '}
              <span style={{ textAlign: 'right' }}>
                {response.instance ? users[response.instance!]?.name : ''}
              </span>
            </MyBadge>
          )}
          <Title color="black">
            {messages.getResponseTitle(response.title!)}
            {messages.answeredBy(
              users[response.userId!],
              i18n.language === 'fa'
            )}
          </Title>
        </div>
        <ResponseItemIcons loading={!!loading}>
          <div>
            {
              <React.Fragment>
                {(canEditSensitive ||
                  (!response.lock && (isResponder || canEdit)) ||
                  withInLockEditDuration) &&
                  isEditableForResponse && (
                    <ItemIcon className="visible-on-hover-inline-flex">
                      <Popover
                        offset={[0, 0]}
                        overlay={i18n.t('edit', { ns: 'survey' }) as string}
                        trigger="hover"
                        placement="bottom"
                      >
                        <div>
                          <ActionLink
                            color="green"
                            onClick={onEditResponse(responseId)}
                          >
                            <Icon type="edit" color={`${black}`} />
                          </ActionLink>
                        </div>
                      </Popover>
                    </ItemIcon>
                  )}
                {canEditSensitive && isEditableForResponse && (
                  <ItemIcon className="visible-on-hover-inline-flex">
                    <Popover
                      offset={[0, 0]}
                      overlay={i18n.t('delete', { ns: 'survey' }) as string}
                      trigger="hover"
                      placement="bottom"
                    >
                      <ActionLink
                        color="green"
                        onClick={openDeleteModal}
                        data-responseid={responseId}
                      >
                        <Icon type={'trash'} color={`${black}`} />
                      </ActionLink>
                    </Popover>
                  </ItemIcon>
                )}
                {(canEditSensitive || (!response.lock && canEdit)) &&
                  isEditableForResponse &&
                  (loading === 'draft' ? (
                    <LoadingIcon customStyle={LoadingStyle} />
                  ) : (
                    <ItemIcon
                      className={
                        response.draft ? '' : 'visible-on-hover-inline-flex'
                      }
                    >
                      <Popover
                        offset={[0, 0]}
                        overlay={messages.draftStatus(response.draft!)}
                        trigger="hover"
                        placement="bottom"
                      >
                        <ActionLink
                          color="green"
                          onClick={handleUpdateResponse(responseId, 'draft')}
                        >
                          <Icon
                            color={`${black}`}
                            type={response.draft ? 'book' : 'book-open'}
                          />
                        </ActionLink>
                      </Popover>
                    </ItemIcon>
                  ))}
              </React.Fragment>
            }
            {survey.surveyType !== 'Exam' && (
              <>
                {loading === 'lock' ? (
                  <LoadingIcon customStyle={LoadingStyle} />
                ) : (
                  <ItemIcon
                    className={
                      response.lock ? '' : 'visible-on-hover-inline-flex'
                    }
                  >
                    <Popover
                      offset={[0, 0]}
                      overlay={messages.lockStatus(response.lock!)}
                      trigger="hover"
                      placement="bottom"
                    >
                      <ActionLink
                        color="green"
                        onClick={
                          canEditSensitive && isEditableForResponse
                            ? handleUpdateResponse(responseId, 'lock')
                            : handleClickEvent
                        }
                      >
                        <Icon
                          color={`${black}`}
                          type={
                            response.lock || !isEditableForResponse
                              ? 'lock'
                              : 'unlock'
                          }
                        />
                      </ActionLink>
                    </Popover>
                  </ItemIcon>
                )}
              </>
            )}
            {!!templates?.length && survey.surveyType !== 'Exam' && (
              <ItemIcon
                className={
                  filteredTasks.length ? '' : 'visible-on-hover-inline-flex'
                }
              >
                <APopover
                  arrow={true}
                  curveBorder={true}
                  trigger="click"
                  show={showTaskPopover}
                  onShowChange={handleShowTaskPopover}
                  target={
                    <ActionLink color="green">
                      <Icon
                        type={'clipboard'}
                        color={filteredTasks.length ? blue : black}
                      />{' '}
                      {filteredTasks.length
                        ? convertDigitToProperFormat(filteredTasks.length)
                        : ''}
                    </ActionLink>
                  }
                >
                  <div>
                    {templates.map((it) => (
                      <ListItem
                        key={it.id}
                        onClick={() => {
                          setShowTaskPopover(false);
                          createTask(
                            response.id!,
                            it.id,
                            it.title,
                            undefined,
                            getAllRelatedFieldsConfigs()
                          );
                        }}
                      >
                        <Icon type="plus" color={`${black}`} />
                        <span>
                          {
                            i18n.t('addTask', {
                              name: it.title,
                              ns: 'survey',
                            }) as string
                          }
                        </span>
                      </ListItem>
                    ))}
                  </div>
                </APopover>
              </ItemIcon>
            )}
          </div>
        </ResponseItemIcons>
        <ItemRowPart
          style={{
            marginInlineStart: '0.5rem',
            flexWrap: 'nowrap',
            display: 'flex',
            flexDirection: 'row-reverse',
            width: '100%',
            justifyContent: 'flex-start',
            gap: '1rem',
            minHeight: '3rem',
          }}
        >
          {isANumber(responseScore) && survey.surveyType !== 'Exam' ? (
            <Popover
              offset={[0, 0]}
              overlay={i18n.t('score', { ns: 'survey' }) as string}
              trigger="hover"
              placement="bottom"
            >
              <ScoreBadge hollow={true} color="darkGray" size={6}>
                <Icon
                  type="star"
                  color={darkGray}
                  style={{ marginInlineEnd: '0.2rem' }}
                />
                <span>
                  {convertNumbersToProperFormat(
                    roundToDynamicFloating(responseScore!),
                    4
                  )}
                </span>
              </ScoreBadge>
            </Popover>
          ) : null}
          {!!response.effectiveDate && !!periodType && (
            <Popover
              offset={[0, 0]}
              overlay={i18n.t('effectiveDate', { ns: 'survey' }) as string}
              trigger="hover"
              placement="bottom"
            >
              <MyBadge hollow={true} color="green" size={9}>
                {getEffectiveName(
                  periodType,
                  response.effectiveDate,
                  i18n.language === 'fa' ? 'fa_IR@calendar=persian' : 'en'
                )}
              </MyBadge>
            </Popover>
          )}
          <Popover
            offset={[0, 0]}
            overlay={i18n.t('submitDate', { ns: 'survey' }) as string}
            trigger="hover"
            placement="bottom"
          >
            <MyBadge hollow={true} size={10} style={{ marginInlineEnd: 0 }}>
              {i18n.language === 'fa'
                ? convertDigitToProperFormat(
                    moment(response.submitDate).format('jYYYY/jM/jD HH:mm')
                  )
                : moment(response.submitDate).format('M/D/YYYY HH:mm')}
            </MyBadge>
          </Popover>
        </ItemRowPart>
      </ItemRow>
      <ItemRow>
        <ItemRowPart>
          {titleAnswers && <MyBadge>{titleAnswers}</MyBadge>}
          {tags && distinctiveQuestions && (
            <DistinctiveAnswers
              answers={response.surveyAnswerList}
              questions={distinctiveQuestions as IMultipleChoiceQuestionView[]}
              tags={tags}
              version={version as ISurveyView}
            />
          )}
        </ItemRowPart>
        {/* {tags && item.data.instanceType === 'User' && (
          <ItemRowPart>
            {response
              .tags!.filter((tagId: number) => tagId > 0)
              .map((tagId: number) => {
                return (
                  <MyBadge
                    key={tagId}
                    color={stringToColour(tags[tagId].text, 1, false)}
                  >
                    {tags[tagId].text}
                  </MyBadge>
                );
              })}
          </ItemRowPart>
        )} */}
      </ItemRow>
      {isActive !== null && !isActive && <Mask />}
    </Item>
  );
};

export default React.memo(ResponseItem, (prevProps, nextProps) => {
  return (
    prevProps.response === nextProps.response &&
    prevProps.selectedItem === nextProps.selectedItem
  );
});
